import { create } from "zustand";
import type { RecordingUiState } from "@/recording/ports";
import type { DesktopDictationStep } from "@/dictation-interactions";

export type ColorScheme = "light" | "dark" | "system";

export type State = {
  paywallVisible:
    | boolean
    | {
        utmSource?: string;
        callback?: () => void;
        title?: string;
        subtitle?: string;
      };
  shouldShowPaywall: boolean;
  isNewSession: boolean;
  colorScheme: ColorScheme;
  alertWidgetSearch?: { placeholder?: string; transcriptId?: string };
  desktopDictationSettingsOpen: boolean;
  desktopDictationSetupRequested?: DesktopDictationStep;
  desktopGuideRequested?: "dictate" | "record";
  desktopRecordingStatus?: "starting" | "recording" | "paused" | "saving";
  // Capture denial outweighs an OS snapshot until an explicit check succeeds.
  desktopAudioPermissionDenied?: Partial<
    Record<"microphone" | "systemAudio", boolean>
  >;
  desktopUploadRequested: boolean;
  desktopRecordRequested: boolean;
  desktopCaptureActive: boolean;
  desktopRecordingStopRequested: boolean;
  desktopRecordingPauseRequested?: "pause" | "resume";
  liveRecording?: RecordingUiState["liveRecording"];
  device: {
    isMobile: boolean;
    width: number;
    height: number;
  };
  alertSelect?: {
    title?: string;
    buttonText?: string;
    defaultOption?: number;
    options: string[];
    onSelect?: (index: number) => void;
    onSelectAsync?: (index: number) => Promise<void>;
  };
  alertLargeFileUpload?: {
    fileName: string;
    filePath: string;
    onComplete: (downloadedFileUrl: string) => Promise<void>;
    onCancel?: () => void;
    onError?: (error: Error) => void;
  };
  alertExportTranscriptOptions?: {
    hasSpeakers: boolean;
    onExport: (options: {
      exportType: "docx" | "pdf" | "txt" | "srt";
      withTimestamps: boolean;
      withSpeakers: boolean;
      withMonologue: boolean;
    }) => void;
  };
  alertPromptSignIn?: {
    title: string;
    subtitle: string;
    placeholder: string;
    shouldPersistBackdropClick?: boolean;
    multiLine?: boolean;
    loading?: boolean;
    hideDismissIcon?: boolean;
    hideCancel?: boolean;
    defaultValue?: string;
    inputMode?: "text" | "email" | "numeric";
    autoComplete?: string;
    pattern?: string;
    button: {
      text: string;
      onPress: (text: string) => void;
      destructive?: boolean;
    };
    onGoogleSignIn?: () => void;
    googleButtonText?: string;
    onDismiss?: () => void;
  };
  alertModal?: {
    privacyLink?: boolean;
    className?: string;
    title: string;
    loading?: boolean;
    subtitle: string;
    shouldPersistBackdropClick?: boolean;
    hideDismissIcon?: boolean;
    hideCancel?: boolean;
    onDismiss?: () => void;
    onLeftButtonPress?: () => void;
    button?: { text: string; onPress: () => void; destructive?: boolean };
    cancelText?: string;
  };
  alertPrompt?: {
    title: string;
    subtitle?: string;
    placeholder: string;
    shouldPersistBackdropClick?: boolean;
    multiLine?: boolean;
    loading?: boolean;
    hideDismissIcon?: boolean;
    hideCancel?: boolean;
    defaultValue?: string;
    // Optional boolean switch rendered under the input.
    toggle?: {
      label: string;
      value: boolean;
      onChange: (next: boolean) => void;
    };
    button: {
      text: string;
      onPress: (text: string) => void;
      destructive?: boolean;
    };
  };
  alertCreateTranscriptFromRecording?: boolean;
  alertWidgetCreateTranscriptFromFileImport?: {
    loading?: boolean;
    selectedFile?: File;
    uploadProgress?: number;
    // A file that arrived from outside the picker (OS "Open with"/share).
    preselectedFile?: {
      name: string;
      size: number;
      type: string;
      path: string;
    };
    onImport: (params: {
      file: File;
      isAudioOrVideoFile: boolean;
      languageCode: string;
      downloadUrl: string;
    }) => Promise<string>;
    onImported: (result: {
      transcriptIds: string[];
      totalFiles: number;
    }) => Promise<void> | void;
  };
  // Audio/video file handed to the app by the OS, waiting for the home
  // surface to route it into the import flow.
  pendingSharedFile?: {
    uri: string;
    name: string;
    mimeType?: string;
    size?: number;
  };
  alertTagPicker?: {
    transcriptIds: string[];
    onComplete?: () => void;
  };
  alertLanguagePicker?: {
    title?: string;
    selectedLanguage?: string;
    onSelect: (languageCode: string, languageName: string) => void;
    onDismiss?: () => void;
  };
};

type Actions = {
  setAlertModal: (payload: State["alertModal"]) => void;
  setLiveRecording: (payload: State["liveRecording"]) => void;
  setIsNewSession: (payload: State["isNewSession"]) => void;
  setAlertLargeFileUpload: (payload: State["alertLargeFileUpload"]) => void;
  setAlertSelect: (payload: State["alertSelect"]) => void;
  setDevice: (payload: State["device"]) => void;
  setPaywallVisible: (payload: State["paywallVisible"]) => void;
  setShouldShowPaywall: (payload: State["shouldShowPaywall"]) => void;
  setAlertExportTranscriptOptions: (
    payload: State["alertExportTranscriptOptions"],
  ) => void;
  setAlertPromptSignIn: (payload: State["alertPromptSignIn"]) => void;
  setAlertPrompt: (payload: State["alertPrompt"]) => void;
  setAlertCreateTranscriptFromRecording: (
    payload: State["alertCreateTranscriptFromRecording"],
  ) => void;
  setAlertCreateWidgetTranscriptFromFileImport: (
    payload: State["alertWidgetCreateTranscriptFromFileImport"],
  ) => void;
  setAlertTagPicker: (payload: State["alertTagPicker"]) => void;
  setAlertLanguagePicker: (payload: State["alertLanguagePicker"]) => void;
  setColorScheme: (payload: ColorScheme) => void;
  setAlertWidgetSearch: (payload: State["alertWidgetSearch"]) => void;
  setDesktopDictationSettingsOpen: (payload: boolean) => void;
  setPendingSharedFile: (payload: State["pendingSharedFile"]) => void;
  reset: () => void;
};

const initialState: State = {
  paywallVisible: false,
  shouldShowPaywall: true,
  isNewSession: true,
  desktopDictationSettingsOpen: false,
  desktopAudioPermissionDenied: undefined,
  desktopUploadRequested: false,
  desktopRecordRequested: false,
  desktopCaptureActive: false,
  desktopRecordingStopRequested: false,
  device: {
    isMobile: false,
    width: window.innerWidth,
    height: window.innerHeight,
  },
  colorScheme: "system",
};

export const useUISlice = create<State & Actions>()((set, get) => ({
  ...initialState,
  setIsNewSession: (isNewSession) => {
    set({ isNewSession });
  },
  setLiveRecording: (payload) => {
    set({ liveRecording: payload });
  },
  setAlertLargeFileUpload: (payload) => {
    set({ alertLargeFileUpload: payload });
  },
  setAlertSelect: (payload) => {
    set({ alertSelect: payload });
  },
  setDevice: (payload) => {
    set({ device: payload });
  },
  setPaywallVisible: (payload) => {
    set({ paywallVisible: payload });
  },
  setShouldShowPaywall: (payload) => {
    set({ shouldShowPaywall: payload });
  },
  setAlertPromptSignIn: (payload) => {
    set({ alertPromptSignIn: payload });
  },
  setAlertExportTranscriptOptions: (payload) => {
    set({ alertExportTranscriptOptions: payload });
  },
  setAlertModal: (payload) => {
    set({ alertModal: payload });
  },
  setAlertPrompt: (payload) => {
    set({ alertPrompt: payload });
  },
  setAlertWidgetSearch: (payload) => {
    set({ alertWidgetSearch: payload });
  },
  setDesktopDictationSettingsOpen: (payload) => {
    set({ desktopDictationSettingsOpen: payload });
  },
  setPendingSharedFile: (payload) => {
    set({ pendingSharedFile: payload });
  },
  setAlertCreateTranscriptFromRecording: (payload) => {
    set({ alertCreateTranscriptFromRecording: payload });
  },
  setAlertCreateWidgetTranscriptFromFileImport: (payload) => {
    set({ alertWidgetCreateTranscriptFromFileImport: payload });
  },
  setAlertTagPicker: (payload) => {
    set({ alertTagPicker: payload });
  },
  setAlertLanguagePicker: (payload) => {
    set({ alertLanguagePicker: payload });
  },
  setColorScheme: (payload) => {
    set({ colorScheme: payload });
  },
  reset: () => {
    set({ ...initialState });
  },
}));
